<template>
  <SearchFilter
    v-if="gradeOptions && subjectOptions && languageOptions"
    :hasActiveFilters="hasActiveFilters"
    :filters="filterConfig"
    :initialFilterValues="filterValues"
    :initialSearchString="searchString"
    @update:filters="handleUpdateFilters"
    @search="onSearch"
    @update:search="updateSearchString"
  ></SearchFilter>

  <v-row>
    <SearchResults
      v-if="isSearching"
      :videos="videos"
      :area-guides="areaGuideResults"
      :selected-subject="selectedSubject"
      :context-id="contextId"
    />
  </v-row>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import SearchResults from '@/components/videos/SearchResults.vue';
import { useAreas, useCategories } from '@ilteducation/content-ref-data';
import type { Category } from '@ilteducation/content-ref-data-types';
import { storeToRefs } from 'pinia';
import { useLocale } from '@/plugins/translation';
import { capitalize, computed, ref, watch, onUnmounted } from 'vue';
import { Filter, type SearchQuery, useAggregateContexts } from '@ilteducation/content-search';
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useVideos } from '@ilteducation/videos';
import useSubjectsWithVideos from '@/composables/use-subjects-with-videos';
import { FilterConfig } from '@ilteducation/videos';
import SearchFilter from '@/components/base/SearchFilter.vue';

const route = useRoute();
const router = useRouter();

const userContext = useUserContext();
const videoStore = useVideos();
const { searchResults } = storeToRefs(videoStore);
const { byId: getCategoryById } = useCategories();
const contextId = ref('');

const aggregationContextStore = useAggregateContexts('Video');
const { aggregate, createAggrId } = aggregationContextStore;
const { context } = storeToRefs(aggregationContextStore);
aggregate(['categories'], {});
const gradeAggregationResult = computed(() => context.value(createAggrId(['categories'], {})));

const { t, l } = useLocale();

const gradeOptions = computed(() => [
  ...(gradeAggregationResult.value?.aggregation?.[0]?.counts.map((c) => getCategoryById.value(c.key)) || [])
    .filter((c): c is Category => !!c && c.kind === 'grade')
    .sort((a, b) => a.category.localeCompare(b.category)),
]);

const { subjectsWithVideos } = useSubjectsWithVideos();
const subjectOptions = computed<Category[]>(() =>
  Array.isArray(subjectsWithVideos.value)
    ? [...subjectsWithVideos.value.toSorted((a, b) => a.category.localeCompare(b.category))]
    : [],
);

const videoLanguagesAggQuery: [string[], Filter] = [['languages'], {}];
const aggrId = createAggrId(...videoLanguagesAggQuery);
aggregate(...videoLanguagesAggQuery);

const languageOptions = computed(() =>
  (
    context
      .value(aggrId)
      ?.aggregation?.[0]?.counts?.map((c) => c.key)
      .map((lang) => ({
        language: capitalize(
          l.languages?.[lang as keyof typeof l.languages]
            ? t(l.languages?.[lang as keyof typeof l.languages] as string)
            : lang,
        ),
        languageCode: lang,
      })) || []
  ).toSorted((a, b) => a.language.localeCompare(b.language)),
);

const searchString = ref((route.query.search as string) || '');
const selectedGrade = ref((route.query.grades as string) || '');
const selectedLanguage = ref((route.query.language as string) || '');
const selectedSubject = ref((route.query.subject as string) || '');

const hasActiveFilters = computed(() => !!(selectedSubject.value || selectedGrade.value || selectedLanguage.value));

const filterConfig = computed<FilterConfig[]>(() => [
  {
    name: 'selectedGrade',
    cols: 12,
    sm: 6,
    md: 3,
    items: gradeOptions.value,
    placeholder: t(l.search.all_grades),
    ariaLabel: t(l.search.filter_by_grade),
    // No specific itemTitle/itemValue needed if not required.
    itemTitle: (c: Category) => `${t(l.grades?.[c.categoryCode as keyof typeof l.grades] || c.category)}`,
    itemValue: 'id',
    extraProps: { density: 'comfortable' },
  },
  {
    name: 'selectedSubject',
    cols: 12,
    sm: 6,
    md: 3,
    items: subjectOptions.value,
    placeholder: t(l.search.all_subjects),
    ariaLabel: t(l.search.filter_by_subject),
    itemTitle: 'category',
    itemValue: 'id',
    extraProps: {
      density: 'comfortable',
    },
  },
  {
    name: 'selectedLanguage',
    cols: 12,
    sm: 6,
    md: 3,
    items: languageOptions.value,
    // If you don't need a label, you can leave placeholder empty.
    placeholder: t(l.search.all_languages),
    ariaLabel: t(l.search.filter_by_language),
    itemTitle: 'language',
    itemValue: 'languageCode',
    extraProps: { density: 'comfortable' },
  },
]);

const filterValues = ref({
  selectedGrade: selectedGrade.value || null,
  selectedSubject: selectedSubject.value || null,
  selectedLanguage: selectedLanguage.value || null,
});

const handleUpdateFilters = (newFilters: Record<string, any>) => {
  selectedGrade.value = newFilters.selectedGrade;
  selectedSubject.value = newFilters.selectedSubject;
  selectedLanguage.value = newFilters.selectedLanguage;
};

const updateSearchString = (newSearchString: string) => {
  searchString.value = newSearchString;
};
const onSearch = (newSearchString: string) => {
  searchString.value = newSearchString;
};

const filterQuery = computed(() => {
  const filter: SearchQuery['filter'] = {};
  const categories: string[] = [];
  if (route.query.subject) {
    categories.push(route.query.subject as string);
  }
  if (route.query.grades) {
    categories.push(route.query.grades as string);
  }
  if (route.query.language) {
    filter.languages = route.query.language as string;
  }
  if (categories.length) {
    filter.categories = categories;
  }
  return filter;
});
const isSearching = computed(() =>
  Object.keys(route.query).some((key) => ['search', 'grades', 'subject', 'language'].includes(key)),
);

const { areas } = useAreas();
const areaGuideResults = computed(() => {
  if (!areas.value.length || searchString.value?.length < 3 || userContext.realm !== 'se') return [];
  return areas.value.filter(
    (area) => area.guide?.active && area.name?.toLowerCase().includes(searchString.value?.toLowerCase()),
  );
});

const videos = computed(() => searchResults.value[contextId.value] ?? []);

const search = () => {
  const sort: SearchQuery['sort'] = [{ field: '_score' }];
  if (!filterQuery.value.subject) {
    sort.push({ field: 'categories', order: 'asc' });
  }
  contextId.value = videoStore.search(
    {
      filter: filterQuery.value,
      q: searchString.value,
      sort,
      pagination: { size: !searchString.value ? 24 : 12 },
      groupFilterMode: 'and',
    },
    !!filterQuery.value.subject,
    true,
  );
};

const debouncedRouterReplace = debounce((query: LocationQueryRaw) => {
  router.replace({ query });
}, 300);

watch([selectedGrade, selectedSubject, selectedLanguage, searchString], () => {
  // we need to set the contextId to some value to show a loading spinner
  contextId.value = 'transition';
  let searchQuery = { ...route.query };
  searchQuery = searchString.value ? { ...searchQuery, search: searchString.value } : omit(searchQuery, 'search');
  searchQuery = selectedGrade.value ? { ...searchQuery, grades: selectedGrade.value } : omit(searchQuery, 'grades');
  searchQuery = selectedLanguage.value
    ? { ...searchQuery, language: selectedLanguage.value }
    : omit(searchQuery, 'language');
  searchQuery = selectedSubject.value
    ? { ...searchQuery, subject: selectedSubject.value }
    : omit(searchQuery, 'subject');
  debouncedRouterReplace(searchQuery);
});

watch(
  () => route.query,
  () => {
    if (isSearching.value) {
      videoStore.searchMode = true;
      search();
    } else {
      videoStore.searchMode = false;
      // resetting the filter
      searchString.value = '';
      selectedGrade.value = '';
      selectedSubject.value = '';
      selectedLanguage.value = '';
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  debouncedRouterReplace.cancel();
  videoStore.searchMode = false;
});
</script>

<style lang="scss" scoped></style>
